import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
	useGetOcrQueueVerify,
	usePostOcrExtractFoliar,
	usePostOcrExtractSoil,
	type OcrFoliarSample,
	type OcrSoilSample,
} from '../../api';
import { OCR_TIMEOUT_MS, OCR_TIMEOUT_MESSAGE } from '../../api/hooks/ocr/ocr.constants';

export type OcrQueueAnalysisType = 'soil' | 'foliar';
export type OcrQueueStatus = 'uploading' | 'queued' | 'processing' | 'done' | 'error';

export interface OcrQueueItem {
	id: string;
	fileName: string;
	analysisType: OcrQueueAnalysisType;
	status: OcrQueueStatus;
	statusChangedAt?: number;
	jobId?: string;
	samples?: Array<OcrSoilSample | OcrFoliarSample>;
	sampleIndex: number;
	error?: string;
}

interface OcrQueueContextValue {
	items: OcrQueueItem[];
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	addFiles: (files: File[], analysisType: OcrQueueAnalysisType) => void;
	getItem: (id: string) => OcrQueueItem | undefined;
	advanceSample: (id: string) => boolean;
	remove: (id: string) => void;
}

const STORAGE_KEY = 'practium:ocr-queue';
const OcrQueueContext = createContext<OcrQueueContextValue | null>(null);
const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readItems = (): OcrQueueItem[] => {
	if (typeof window === 'undefined') return [];
	try {
		const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
		return Array.isArray(value)
			? value.map((item) => ({ ...item, sampleIndex: item.sampleIndex ?? 0, statusChangedAt: item.statusChangedAt ?? Date.now() }))
			: [];
	} catch {
		return [];
	}
};

export const OcrQueueProvider = ({ children }: { children: ReactNode }) => {
	const [items, setItems] = useState<OcrQueueItem[]>(readItems);
	const [isOpen, setIsOpen] = useState(true);
	const soilMutation = usePostOcrExtractSoil();
	const foliarMutation = usePostOcrExtractFoliar();

	useEffect(() => {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
	}, [items]);

	useEffect(() => {
		const activeItems = items.filter((item) =>
			['uploading', 'queued', 'processing'].includes(item.status)
		);
		if (!activeItems.length) return;
		const deadline = Math.min(...activeItems.map((item) => (item.statusChangedAt ?? Date.now()) + OCR_TIMEOUT_MS));
		const timer = window.setTimeout(() => {
			const now = Date.now();
			setItems((current) => current.map((item) =>
				['uploading', 'queued', 'processing'].includes(item.status) &&
				now - (item.statusChangedAt ?? now) >= OCR_TIMEOUT_MS
					? { ...item, status: 'error', error: OCR_TIMEOUT_MESSAGE }
					: item
			));
		}, Math.max(0, deadline - Date.now()));
		return () => window.clearTimeout(timer);
	}, [items]);

	const pendingItems = items.filter(
		(item) => item.jobId && (item.status === 'queued' || item.status === 'processing')
	);
	const verificationQueries = useGetOcrQueueVerify(pendingItems);

	useEffect(() => {
		verificationQueries.forEach((query, index) => {
			const item = pendingItems[index];
			if (!item || !query.data) return;
			const response = query.data;
			const nextStatus: OcrQueueStatus = response.status === 'failed' ? 'error' : response.status;
			if (item.status === nextStatus && (nextStatus !== 'done' || item.samples)) return;
			setItems((current) =>
				current.map((entry) =>
					entry.id === item.id && (entry.status === 'queued' || entry.status === 'processing')
						? {
							...entry,
							status: nextStatus,
							statusChangedAt: Date.now(),
							samples: response.result?.samples,
							error: response.error?.message,
						}
						: entry
				)
			);
		});
	}, [verificationQueries, pendingItems]);

	const addFiles = (files: File[], analysisType: OcrQueueAnalysisType) => {
		const newItems = files.map((file) => ({
			id: createId(),
			fileName: file.name,
			analysisType,
			status: 'uploading' as const,
			statusChangedAt: Date.now(),
			sampleIndex: 0,
		}));
		setItems((current) => [...current, ...newItems]);

		newItems.forEach(async (item, index) => {
			try {
				const mutation = analysisType === 'soil' ? soilMutation : foliarMutation;
				const response = await mutation.mutateAsync({ file: files[index] });
				setItems((current) =>
					current.map((entry) =>
						entry.id === item.id && entry.status === 'uploading'
							? { ...entry, status: 'queued', jobId: response.jobId, statusChangedAt: Date.now() }
							: entry
					)
				);
			} catch (error) {
				setItems((current) =>
					current.map((entry) =>
						entry.id === item.id && entry.status === 'uploading'
							? {
									...entry,
									status: 'error',
									error: error instanceof Error ? error.message : 'Falha no envio',
								  }
							: entry
					)
				);
			}
		});
	};

	const value = useMemo(
		() => ({
			items,
			isOpen,
			setIsOpen,
			addFiles,
			getItem: (id: string) => items.find((item) => item.id === id),
			advanceSample: (id: string) => {
				const item = items.find((entry) => entry.id === id);
				if (!item?.samples || item.sampleIndex >= item.samples.length - 1) return false;
				setItems((current) =>
					current.map((entry) =>
						entry.id === id ? { ...entry, sampleIndex: entry.sampleIndex + 1 } : entry
					)
				);
				return true;
			},
			remove: (id: string) => setItems((current) => current.filter((item) => item.id !== id)),
			clearFinished: () =>
				setItems((current) => current.filter((item) => item.status !== 'done' && item.status !== 'error')),
		}),
		[items, isOpen]
	);

	return <OcrQueueContext.Provider value={value}>{children}</OcrQueueContext.Provider>;
};

export const useOcrQueue = () => {
	const context = useContext(OcrQueueContext);
	if (!context) throw new Error('useOcrQueue deve ser usado dentro de OcrQueueProvider');
	return context;
};
